
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

const API_BASE = 'https://guardiancloudcare.com/backend/api';

type SupervisorNotification = {
  id: number;
  recipient_uid: string;
  sender_uid: string | null;
  type: string;
  title: string;
  message: string;
  reference_type: string | null;
  reference_id: number | string | null;
  is_read: boolean;
  created_at: string;
  report_uid: string | null;
};

export default function SupNotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const supervisorUid = user?.uid;

  const [notifications, setNotifications] = useState<
    SupervisorNotification[]
  >([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const loadNotifications = useCallback(async (showLoading = false) => {
    if (!supervisorUid) {
      setError('Supervisor account information is unavailable.');
      setLoading(false);
      return;
    }

    if (showLoading) setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE}/get_supervisor_notifications.php?supervisor_uid=${encodeURIComponent(
          supervisorUid
        )}&limit=100`,
        { headers: { Accept: 'application/json' } }
      );

      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.message || 'Unable to load notifications.');
      }

      setNotifications(
        Array.isArray(json.data?.notifications)
          ? json.data.notifications
          : []
      );
      setUnreadCount(Number(json.data?.unread_count ?? 0));
      setError('');
    } catch (err) {
      console.error('Supervisor notification error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load notifications.'
      );
    } finally {
      setLoading(false);
    }
  }, [supervisorUid]);

  useEffect(() => {
    void loadNotifications(true);

    const interval = window.setInterval(() => {
      void loadNotifications();
    }, 15000);

    return () => window.clearInterval(interval);
  }, [loadNotifications]);

  const markAsRead = async (notification: SupervisorNotification) => {
    if (notification.is_read || updatingId === notification.id) return;

    if (!supervisorUid) return;

    setUpdatingId(notification.id);

    try {
      const response = await fetch(
        `${API_BASE}/mark_supervisor_notification_read.php`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            supervisor_uid: supervisorUid,
            notification_id: notification.id,
          }),
        }
      );

      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.message || 'Unable to mark notification as read.');
      }

      setNotifications((previous) =>
        previous.map((item) =>
          item.id === notification.id
            ? { ...item, is_read: true }
            : item
        )
      );

      setUnreadCount((count) => Math.max(0, count - 1));
    } catch (err) {
      console.error('Mark notification as read error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to update notification.'
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const openNotification = async (
    notification: SupervisorNotification
  ) => {
    await markAsRead(notification);

    const reportUid = notification.report_uid;

    if (reportUid) {
      navigate(`/sup-victim-reports/${encodeURIComponent(reportUid)}`);
      return;
    }

    // Existing report notifications may contain their report ID in the message.
    const match = notification.message.match(
      /Report ID:\s*([A-Za-z0-9_-]+)/i
    );

    if (match?.[1]) {
      navigate(`/sup-victim-reports/${encodeURIComponent(match[1])}`);
      return;
    }

    // Fall back to the existing supervisor case assignment page.
    navigate('/sup-case-assignment');
  };

  const visibleNotifications = notifications.filter(
    (item) => filter === 'all' || !item.is_read
  );

  const formatDate = (value: string) => {
    const date = new Date(value.replace(' ', 'T'));

    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleString();
  };

  return (
    <AppLayout
      title="Supervisor Notifications"
      breadcrumb={['Supervisor', 'Notifications']}
    >
      <div className="mx-auto max-w-5xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Notifications
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Updates for reports and cases assigned to your office.
            </p>
          </div>

          <button
            type="button"
            onClick={() => void loadNotifications(true)}
            disabled={loading}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:border-teal-300 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-gray-100 bg-white p-5">
            <p className="text-sm text-gray-500">Total loaded</p>
            <p className="mt-1 text-3xl font-bold text-gray-900">
              {notifications.length}
            </p>
          </div>

          <div className="rounded-xl border border-amber-100 bg-amber-50 p-5">
            <p className="text-sm text-amber-700">Unread notifications</p>
            <p className="mt-1 text-3xl font-bold text-amber-800">
              {unreadCount}
            </p>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              filter === 'all'
                ? 'bg-teal-600 text-white'
                : 'border border-gray-200 bg-white text-gray-600'
            }`}
          >
            All
          </button>

          <button
            type="button"
            onClick={() => setFilter('unread')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              filter === 'unread'
                ? 'bg-teal-600 text-white'
                : 'border border-gray-200 bg-white text-gray-600'
            }`}
          >
            Unread ({unreadCount})
          </button>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
          {loading && notifications.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">
              Loading notifications...
            </p>
          ) : visibleNotifications.length === 0 ? (
            <div className="p-10 text-center">
              <i className="ri-notification-off-line text-3xl text-gray-300" />
              <p className="mt-3 font-medium text-gray-700">
                No notifications found
              </p>
              <p className="mt-1 text-sm text-gray-500">
                New report and case updates will appear here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {visibleNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex gap-4 p-5 transition-colors ${
                    notification.is_read ? 'bg-white' : 'bg-teal-50/50'
                  }`}
                >
                  <div
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
                      notification.is_read
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-teal-100 text-teal-700'
                    }`}
                  >
                    <i className="ri-notification-3-line text-lg" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-gray-900">
                        {notification.title}
                      </h3>

                      {!notification.is_read && (
                        <span className="rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">
                          New
                        </span>
                      )}
                    </div>

                    <p className="mt-1 whitespace-pre-line break-words text-sm leading-6 text-gray-600">
                      {notification.message}
                    </p>

                    <p className="mt-2 text-xs text-gray-400">
                      {formatDate(notification.created_at)}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void openNotification(notification)}
                        className="rounded-lg bg-teal-600 px-3 py-2 text-xs font-medium text-white hover:bg-teal-700"
                      >
                        View details
                      </button>

                      {!notification.is_read && (
                        <button
                          type="button"
                          disabled={updatingId === notification.id}
                          onClick={() => void markAsRead(notification)}
                          className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {updatingId === notification.id
                            ? 'Updating...'
                            : 'Mark as read'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400">
          Notifications refresh automatically every 15 seconds.
        </p>
      </div>
    </AppLayout>
  );
}
